import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import { useContext, useEffect, useMemo, useRef } from 'react';
import LogoutIcon from '@/components/icons/LogoutIcon';
import UserProfileIcon from '@/components/icons/UserProfileIcon';
import SubHeading, { VARIANT as SUBHEADING_TYPE } from '@/components/SubHeading';
import { NAVIGATION_FROM_APP_CODE } from '@/constants/common';
import { AuthContext } from '@/context/AuthProvider';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import { AIMS_APP_CODE } from '@/models/Auth';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
// import { trackCustomEvent } from '@/utils/analytics/googleAnalytics'
import { getCookieValue } from '@/utils/helper';
import Form from './Form';
import Header from './Header';
import MenuItemList from './MenuItemList';
interface Props {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}
const SideDrawer = ({
  isOpen,
  setIsOpen
}: Props) => {
  const {
    t
  } = useTranslation('common');
  const sideDrawerRef = useRef<HTMLElement>(null);
  const isLogoutBtnHided = getCookieValue(NAVIGATION_FROM_APP_CODE) === AIMS_APP_CODE;
  const {
    isAuthReady,
    isAuthenticated,
    logout
  } = useContext(AuthContext);
  const {
    trackCustomEvent
  } = useTriggerEvents();
  const onClose = () => {
    setIsOpen(false);
    trackCustomEvent(CustomEventTypes.HAMBRUGER_MENU_SELECTED, 'Cross Icon');
  };
  const onLogout = () => {
    trackCustomEvent(CustomEventTypes.HAMBRUGER_MENU_SELECTED, 'Log Out');
    if (logout) {
      logout();
    }
  };
  const userNameOrLogin = useMemo(() => isAuthenticated ? <Form isOpen={isOpen} /> : <Link href="/login" data-testid="profile-login-button">
          {t('login')}
        </Link>, [isAuthenticated, isOpen, t]);
  useEffect(() => {
    if (isOpen) document.body.style.overflowY = 'hidden';
    return () => {
      document.body.style.overflowY = 'auto';
    };
  }, [isOpen]);
  useEffect(() => {
    if (sideDrawerRef && sideDrawerRef.current) {
      sideDrawerRef.current.style.display = 'block';
    }
  }, []);
  return <>
      <section data-testid={`side-drawer-section-${isOpen ? 'open' : 'close'}`} className="z-50 fixed inset-0 bg-gray-900 bg-opacity-25 p-0 overflow-hidden transition-all duration-300 ease-in-out" style={{
      opacity: isOpen ? 1 : 0,
      visibility: isOpen ? 'visible' : 'hidden'
    }}>
        <button data-testid="profile-drawer-outside-button" className="w-screen h-full" onClick={onClose} />
      </section>
      <section ref={sideDrawerRef} style={{
      transform: `translateX(${isOpen ? '0px' : '100%'})`,
      display: 'none'
    }} className="top-0 right-0 z-50 fixed bg-white shadow-xl min-w-[360px] max-w-sm h-full text-white transition-all duration-300 ease-in-out delay-100">
        <article className="relative flex flex-col max-w-lg h-full overflow-y-scroll text-lg">
          <Header handleClose={() => setIsOpen(false)} data-sentry-element="Header" data-sentry-source-file="SideDrawer.tsx" />
          <div className="flex justify-between items-center bg-primary-110 px-6 py-4 h-14">
            <div className="flex items-center gap-3 basis-full">
              <UserProfileIcon className="fill-white w-8 h-8" data-sentry-element="UserProfileIcon" data-sentry-source-file="SideDrawer.tsx" />
              {isAuthReady ? userNameOrLogin : null}
            </div>
          </div>

          <MenuItemList onItemClick={() => setIsOpen(false)} data-sentry-element="MenuItemList" data-sentry-source-file="SideDrawer.tsx" />

          {!isLogoutBtnHided && isAuthenticated && <footer data-testid="profile-logout-footer" className="flex justify-start gap-4 bg-neutral-10 p-4 h-14 text-neutral-70 cursor-pointer" onClick={onLogout}>
              <LogoutIcon className="stroke-1.5 stroke-neutral-80 ml-2 h-6" />
              <SubHeading variant={SUBHEADING_TYPE.MEDIUM}>
                {t('log_out')}
              </SubHeading>
            </footer>}
        </article>
      </section>
    </>;
};
export default SideDrawer;